import { Injectable } from '@angular/core';
import { CoralogixRum, CoralogixLogSeverity } from '@coralogix/browser';
import { environment } from 'src/environments/environment';
import { EventPipelineService } from './event-pipeline.service';
import { Preferences } from '@capacitor/preferences';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private isInitialized = false;
  private lastUserId: string | null = null;
  private lastUserName?: string;
  private lastEmail?: string;
  private pendingUserContext: { userId: string; userName?: string; email?: string } | null = null;

  constructor(private eventPipelineService: EventPipelineService) {}

  /**
   * Initialize Coralogix RUM
   * Should be called once during app startup
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    try {
      CoralogixRum.init({
        application: 'vessel-client-app',
        version: environment.appVersion,
        public_key: environment.coralogixApiKey,
        environment: environment.environmentName,
        coralogixDomain: 'US2',
      });
      this.isInitialized = true;
      // Replay any user context that arrived before RUM finished initializing.
      if (this.pendingUserContext) {
        const pending = this.pendingUserContext;
        this.pendingUserContext = null;
        this.setUserContext(pending.userId, pending.userName, pending.email);
      }
      // console.log(`[Analytics] Coralogix initialized for environment: ${environment.environmentName}`);
    } catch (error) {
      console.error('[Analytics] Failed to initialize Coralogix:', error);
    }
  }

  /**
   * Set user context after authentication
   * @param userId - Cognito user ID (sub claim)
   * @param userName - User's display name
   * @param email - User's email address
   */
  setUserContext(userId: string, userName?: string, email?: string): void {
    if (!userId) {
      return;
    }

    // Merge with last-known enrichment so an id-only call (e.g. a session
    // refresh) doesn't wipe a name/email set by an earlier enriched call.
    if (userId === this.lastUserId) {
      userName = userName ?? this.lastUserName;
      email = email ?? this.lastEmail;
    }
    this.lastUserId = userId;
    this.lastUserName = userName ?? this.lastUserName;
    this.lastEmail = email ?? this.lastEmail;

    // The AWS event pipeline is independent of Coralogix init, so attribute it
    // immediately — this is the signal the engagement corpus reads.
    try {
      this.eventPipelineService.setUserContext(
        userId,
        environment.appVersion,
        this.detectOS(),
        this.detectOSVersion()
      );
    } catch (error) {
      console.error('[Analytics] Failed to set pipeline user context:', error);
    }

    // Coralogix RUM needs init() first; stash and replay once it's ready.
    if (!this.isInitialized) {
      this.pendingUserContext = { userId, userName, email };
      return;
    }

    try {
      CoralogixRum.setUserContext({
        user_id: userId,
        user_name: userName || 'Unknown',
        user_email: email || '',
      });
    } catch (error) {
      console.error('[Analytics] Failed to set user context:', error);
    }
  }

  /**
   * Clear user context on logout
   */
  clearUserContext(): void {
    this.lastUserId = null;
    this.lastUserName = undefined;
    this.lastEmail = undefined;
    this.pendingUserContext = null;

    // Flush queued events under the outgoing user, then clear — regardless of
    // whether Coralogix RUM finished initializing.
    try {
      this.eventPipelineService.flush();
      this.eventPipelineService.clearUserContext();
    } catch (error) {
      console.error('[Analytics] Failed to clear pipeline user context:', error);
    }

    if (!this.isInitialized) {
      return;
    }
    try {
      CoralogixRum.setUserContext({
        user_id: '',
        user_name: '',
        user_email: '',
      });
    } catch (error) {
      console.error('[Analytics] Failed to clear user context:', error);
    }
  }

  /**
   * Lightweight auth-lifecycle telemetry (session expiry, attribute failures).
   * Coralogix-only; no-ops until RUM is initialized. Never throws.
   */
  logAuthEvent(event: string, details?: Record<string, any>): void {
    if (!this.isInitialized) {
      return;
    }
    try {
      CoralogixRum.log(CoralogixLogSeverity.Info, 'Auth Event', { event, ...(details || {}) });
    } catch {
      // never break the app on telemetry
    }
  }

  /**
   * Track widget interaction on homepage
   * @param widget - Widget name (arm_slider, lights, thermostat, alerts, survey)
   * @param action - Action performed (e.g., "toggle", "arm_initiated", "temperature_change")
   * @param value - Optional additional data
   */
  trackWidgetInteraction(widget: string, action: string, value?: Record<string, any>): void {
    try {
      this.eventPipelineService.trackEvent({
        type: 'widget_interaction',
        timestamp: Date.now(),
        widget,
        action,
        ...value
      });
      // VI-214: mirror MQTT command-telemetry to Coralogix so all observability
      // stages converge there (HA stages already arrive via the log-forwarder).
      // CoralogixRum.log no-ops until RUM init and never throws.
      if (widget === 'mqtt') {
        CoralogixRum.log(CoralogixLogSeverity.Info, 'Widget Interaction', { widget, action, ...value });
      }
      this.trackFirstUse(`widget:${widget}`);
    } catch (error) {
      console.error('[Analytics] Failed to track widget interaction:', error);
    }
  }

  /**
   * Track settings changes
   * @param setting - Setting name
   * @param value - New value of the setting
   * @param category - Settings category (general, lighting, climate, notifications, access, shower)
   */
  trackSettingChange(setting: string, value: any, category: string): void {
    try {
      this.eventPipelineService.trackEvent({
        type: 'setting_change',
        timestamp: Date.now(),
        setting,
        value,
        category
      });
      this.trackFirstUse(`setting:${category}`);
    } catch (error) {
      console.error('[Analytics] Failed to track setting change:', error);
    }
  }

  /**
   * Track permission statuses
   * @param permissions - Object with permission status values
   */
  trackPermissionStatus(permissions: Record<string, string>): void {
    try {
      this.eventPipelineService.trackEvent({
        type: 'permission_status',
        timestamp: Date.now(),
        permissions
      });
      // Also log via Coralogix RUM so daily aggregator can query it
      if (this.isInitialized) {
        CoralogixRum.log(CoralogixLogSeverity.Info, 'Permission Status', { permissions });
      }
    } catch (error) {
      console.error('[Analytics] Failed to track permission status:', error);
    }
  }

  /**
   * Track screen/page view
   * @param screenName - Name of the screen viewed
   * @param route - The route path
   */
  trackScreenView(screenName: string, route: string): void {
    try {
      this.eventPipelineService.trackEvent({
        type: 'screen_view',
        timestamp: Date.now(),
        screenName,
        route
      });
    } catch (error) {
      console.error('[Analytics] Failed to track screen view:', error);
    }
  }

  /**
   * Onboarding "Set up your home" events. userId/sessionId/appVersion are attached
   * by the pipeline; callers pass propertyId/moveInDate/itemId etc. in `fields`.
   */
  trackOnboardingEvent(eventType: string, fields: Record<string, any> = {}): void {
    try {
      this.eventPipelineService.trackEvent({
        type: eventType,
        timestamp: Date.now(),
        ...fields,
      });
    } catch (error) {
      console.error('[Analytics] Failed to track onboarding event:', error);
    }
  }

  /**
   * Track first-time feature usage
   * Fires a first_use event only once per feature per device
   * @param featureId - Unique feature identifier
   */
  async trackFirstUse(featureId: string): Promise<void> {
    try {
      const key = `first_use_${featureId}`;
      const { value } = await Preferences.get({ key });
      if (value) {
        return; // Already tracked
      }
      await Preferences.set({ key, value: 'true' });
      this.eventPipelineService.trackEvent({
        type: 'first_use',
        timestamp: Date.now(),
        featureId
      });
    } catch (error) {
      console.error('[Analytics] Failed to track first use:', error);
    }
  }

  /**
   * Track push notification tap
   * @param notificationData - Data from the notification payload
   */
  trackNotificationTap(notificationData: Record<string, any>): void {
    try {
      this.eventPipelineService.trackEvent({
        type: 'notification_tap',
        timestamp: Date.now(),
        ...notificationData
      });
    } catch (error) {
      console.error('[Analytics] Failed to track notification tap:', error);
    }
  }

  /**
   * Track MQTT connection health event
   * @param action - The connection event (connected, disconnected, error, reconnect)
   * @param details - Optional additional details
   */
  trackMqttHealth(action: string, details?: Record<string, any>): void {
    try {
      this.eventPipelineService.trackEvent({
        type: 'mqtt_health',
        timestamp: Date.now(),
        action,
        ...details
      });
    } catch (error) {
      console.error('[Analytics] Failed to track MQTT health:', error);
    }
  }

  // ============================================
  // Phase 3: Additional Tracking Methods
  // ============================================

  /**
   * Track appearance mode changes (light/dark/auto)
   * @param mode - The appearance mode selected
   */
  trackAppearanceChange(mode: 'light' | 'dark' | 'auto'): void {
    this.trackSettingChange('appearance_mode', mode, 'general');
  }

  /**
   * Track 2N intercom events (voice calls)
   * @param action - The intercom action performed
   * @param deviceId - Optional device/intercom ID
   */
  trackIntercomEvent(
    action: 'call_initiated' | 'call_answered' | 'call_ignored' | 'call_ended' | 'door_unlocked',
    deviceId?: string
  ): void {
    this.trackWidgetInteraction('intercom', action, deviceId ? { deviceId } : undefined);
  }

  /**
   * Track navigation/location actions
   * @param action - The navigation action performed
   * @param destination - Optional destination info
   */
  trackNavigationAction(
    action: 'get_directions' | 'share_address' | 'open_maps',
    destination?: string
  ): void {
    this.trackWidgetInteraction('navigation', action, destination ? { destination } : undefined);
  }

  /**
   * Track message/notification interactions
   * @param action - The action performed on the message
   * @param messageId - The message identifier
   */
  trackMessageInteraction(
    action: 'viewed' | 'marked_read' | 'dismissed' | 'action_clicked',
    messageId?: string
  ): void {
    this.trackWidgetInteraction('message', action, messageId ? { messageId } : undefined);
  }

  /**
   * Track alarm system interactions
   * @param action - The alarm action performed
   * @param previousState - Previous alarm state
   * @param newState - New alarm state
   */
  trackAlarmInteraction(
    action: 'arm_initiated' | 'arm_completed' | 'disarm_initiated' | 'disarm_completed' | 'arm_failed',
    previousState?: string,
    newState?: string
  ): void {
    this.trackWidgetInteraction('alarm', action, {
      ...(previousState && { previousState }),
      ...(newState && { newState })
    });
  }

  /**
   * Track survey/feedback interactions
   * @param action - The survey action performed
   * @param surveyId - Optional survey identifier
   * @param rating - Optional rating value
   */
  trackSurveyInteraction(
    action: 'started' | 'completed' | 'abandoned' | 'question_answered',
    surveyId?: string,
    rating?: number
  ): void {
    this.trackWidgetInteraction('survey', action, {
      ...(surveyId && { surveyId }),
      ...(rating !== undefined && { rating })
    });
  }

  /**
   * Track early access feedback submission
   * @param featureId - The feature being reviewed
   * @param rating - User rating (1-5)
   * @param hasComment - Whether feedback includes a comment
   */
  trackEarlyAccessFeedback(featureId: string, rating: number, hasComment: boolean): void {
    this.trackWidgetInteraction('early_access', 'feedback_submitted', {
      featureId,
      rating,
      hasComment
    });
  }

  /**
   * VI-190: resident's answer to "did this solve it?".
   * First chat event to carry a chat id, so outcomes can be joined to the answer.
   */
  trackChatFeedback(
    action: 'feedback_submitted' | 'escalation_opened',
    fields: {
      chatId?: string;
      threadId?: string;
      outcome?: string;
      reasonCode?: string;
      channel?: string;
    }
  ): void {
    this.trackWidgetInteraction('vessel_intelligence', action, fields);
  }

  private detectOS(): string {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'web';
  }

  private detectOSVersion(): string {
    const ua = navigator.userAgent;
    const iosMatch = ua.match(/OS (\d+[_\.]\d+)/);
    if (iosMatch) return iosMatch[1].replace('_', '.');
    const androidMatch = ua.match(/Android (\d+\.?\d*)/);
    if (androidMatch) return androidMatch[1];
    return 'unknown';
  }
}
